import React from "react";
import Fuse from "fuse.js";
import CartContext from "./CartContext";

export default class RawMaterialPendingPurchaseItems extends React.Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      items: [],
      fuse: null,
      loading: true,
      error: null,
      search: "",
      filteredItems: [],
    };
  }

  componentDidMount() {
    this.loadData();
  }

  apiBase() {
    const base = process.env.REACT_APP_API || "/";
    return base.endsWith("/") ? base : base + "/";
  }

  effectiveStoreId() {
    if (this.props.storeId) return this.props.storeId;
    if (this.context && this.context.storeId) return this.context.storeId;
    return null;
  }

  loadData() {
    const storeId = this.effectiveStoreId();
    if (!storeId) {
      this.setState({
        error:
          "storeId not provided. Pass storeId as a prop or provide it via React context.",
        loading: false,
      });
      return;
    }

    const url = `${this.apiBase()}RmPurchasePending/pending-items/${encodeURIComponent(
      storeId
    )}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch pending items`);
        return res.json();
      })
      .then((data) => {
        const items = Array.isArray(data) ? data : [];
        const fuse = new Fuse(items, {
          keys: [
            "purchaseOrderNo",
            "warehouseName",
            "supplierName",
            "itemName",
            "createdBy",
          ],
          threshold: 0, // strict; raise to 0.3-0.5 if you want looser matches
          includeScore: false,
        });
        this.setState({
          items,
          fuse,
          filteredItems: items,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        this.setState({ error: err.message, loading: false });
      });
  }

  formatNumber(val) {
    if (val === null || val === undefined) return "";
    const num = Number(val);
    if (Number.isNaN(num)) return "";
    if (Number.isInteger(num)) return num.toString();
    return num.toFixed(2);
  }

  onSearchChange = (e) => {
    const search = e.target.value;
    this.setState({ search });
    this.applySearch(search);
  };

  applySearch(search) {
    const { fuse, items } = this.state;
    if (!fuse || !search.trim()) {
      this.setState({ filteredItems: items });
      return;
    }

    const results = fuse.search(search.trim());
    const filtered = results.map((r) => r.item);
    this.setState({ filteredItems: filtered });
  }

  // CSV helpers
  escapeCsvCell(value) {
    if (value === null || value === undefined) return "";
    // convert date objects and numbers to string
    if (value instanceof Date) value = value.toISOString();
    const s = String(value);
    // If it contains quotes, commas, newlines -> wrap in quotes and escape quotes
    if (/[,"\n\r]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  }

  downloadCsv = () => {
    const { filteredItems } = this.state;
    if (!filteredItems || filteredItems.length === 0) {
      window.alert("No rows to export.");
      return;
    }

    // Column headers - friendly names
    const headers = [
      "PurchaseOrderNo",
      "Created",
      "CreatedBy",
      "WarehouseName",
      "SupplierName",
      "SupplierMobile",
      "ItemName",
      "QtyRequested",
      "QtyReceived",
      "Pending",
    ];

    const rows = filteredItems.map((it) => {
      const createdVal =
        it.created ?? it.Created
          ? new Date(it.created ?? it.Created).toISOString()
          : "";
      return [
        it.purchaseOrderNo ?? it.PurchaseOrderNo ?? "",
        createdVal,
        it.createdBy ?? it.CreatedBy ?? "",
        it.warehouseName ?? it.WarehouseName ?? "",
        it.supplierName ?? it.SupplierName ?? "",
        it.supplierMobile ?? it.SupplierMobile ?? "",
        it.itemName ?? it.ItemName ?? "",
        it.qtyRequested ?? it.QtyRequested ?? "",
        it.qtyReceived ?? it.QtyReceived ?? "",
        it.pending ?? it.Pending ?? "",
      ]
        .map((c) => this.escapeCsvCell(c))
        .join(",");
    });

    // Build CSV string (UTF-8 BOM to help Excel detect UTF-8)
    const bom = "\uFEFF";
    const csv = bom + headers.join(",") + "\n" + rows.join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    // filename contains storeId and timestamp
    const storeId = this.effectiveStoreId() || "store";
    const ts = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .replace("T", "_")
      .split("Z")[0];
    const filename = `pending_purchase_items_${storeId}_${ts}.csv`;

    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  render() {
    const { loading, error, filteredItems, search } = this.state;

    return (
      <div className="mx-5 my-3">
        <div className="shadow d-flex justify-content-between align-items-center mb-2 p-3">
          <h3 className="mb-0 ">RawMaterial Pending Purchase Items</h3>

          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <input
              type="text"
              className="form-control"
              style={{ minWidth: 300 }}
              placeholder="Search PO / Warehouse / Supplier / Item / CreatedBy"
              value={search}
              onChange={this.onSearchChange}
            />

            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                // clear search quickly
                this.setState({ search: "" });
                this.applySearch("");
              }}
              title="Clear search"
            >
              Clear
            </button>

            <button
              className="btn btn-primary"
              onClick={this.downloadCsv}
              title="Download shown rows as CSV"
            >
              Download CSV
            </button>
          </div>
        </div>

        {loading && <div className="alert alert-info">Loading...</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && (
          <div className="table-responsive">
            <table className="table table-striped table-sm">
              <thead className="table-light">
                <tr>
                  <th>PO No</th>
                  <th>Created</th>
                  <th>CreatedBy</th>
                  <th>Warehouse</th>
                  <th>Supplier</th>
                  <th>Supplier Contact</th>
                  <th>Item</th>
                  <th style={{ textAlign: "right" }}>Qty Requested (A)</th>
                  <th style={{ textAlign: "right" }}>Qty Received (B)</th>
                  <th style={{ textAlign: "right" }}>Pending (A - B)</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center">
                      No pending items found.
                    </td>
                  </tr>
                )}
                {filteredItems.map((it) => (
                  <tr
                    key={`${it.purchaseRequestId || it.PurchaseRequestId}-${
                      it.itemId || it.ItemId
                    }`}
                  >
                    <td>{it.purchaseOrderNo ?? it.PurchaseOrderNo}</td>
                    <td>
                      {it.created ?? it.Created
                        ? new Date(it.created ?? it.Created).toLocaleString()
                        : ""}
                    </td>
                    <td>{it.createdBy ?? it.CreatedBy}</td>
                    <td>{it.warehouseName ?? it.WarehouseName}</td>
                    <td>{it.supplierName ?? it.SupplierName}</td>
                    <td>{it.supplierMobile ?? it.SupplierMobile}</td>
                    <td>{it.itemName ?? it.ItemName}</td>
                    <td style={{ textAlign: "right" }}>
                      {this.formatNumber(it.qtyRequested ?? it.QtyRequested)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {this.formatNumber(it.qtyReceived ?? it.QtyReceived)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {this.formatNumber(it.pending ?? it.Pending)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}
